import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from "@codemirror/autocomplete";
import { defaultKeymap, history, insertNewlineAndIndent } from "@codemirror/commands";
import { bracketMatching, indentOnInput, syntaxHighlighting } from "@codemirror/language";
import { lintKeymap } from "@codemirror/lint";
import { Compartment, EditorState, type Extension, Prec } from "@codemirror/state";
import { EditorView, highlightSpecialChars, keymap, placeholder } from "@codemirror/view";
import { PromQLExtension, newCompleteStrategy } from "@prometheus-io/codemirror-promql";
import { isShallowEqual } from "@suse-o11y/is-shallow-equal";
import { Logger } from "@suse-o11y/logger";
import { min } from "@suse-o11y/min";
import { useEffect } from "@suse-o11y/use-effect";
import { useHandler } from "@suse-o11y/use-handler";
import noop from "lodash/noop";
import { type Dispatch, type RefObject, useRef } from "react";

import { fetch } from "../../generic/fetch";

import { type CompletionType, PromQLCompleteStrategy } from "./_promql-complete-strategy";
import { promQLHighlighter } from "./theme";

const dynamicConfigCompartment = new Compartment();
const logger = Logger.namespace("useCodeMirror");

export interface UseCodeMirrorParams extends Partial<Omit<InitEditorStateParams, "initialValue" | "dynamic">> {
	/**
	 * A text that will be visible in the editor.
	 * Updates the editor when changed.
	 *
	 * @default ""
	 */
	value?: string;

	/**
	 * @default false
	 */
	readonly?: boolean;

	/**
	 * @default true
	 */
	lint?: boolean;

	/**
	 * A list of completion types to show in the autocomplete suggestions.
	 * When empty, all types are shown.
	 *
	 * @default []
	 */
	completionTypes?: ReadonlyArray<CompletionType>;
}

export const useCodeMirror = (
	ref: RefObject<HTMLElement>,
	{
		onChange,
		onFocus,
		onBlur,
		onExecute,
		value = "",
		readonly = false,
		lint = true,
		completionTypes = [],
	}: UseCodeMirrorParams,
): null | EditorView => {
	const viewRef = useRef<null | EditorView>(null);
	const initialValueRef = useRef(value);
	const stableOnExecute = useHandler(onExecute);
	const stableOnFocus = useHandler(onFocus);
	const stableOnBlur = useHandler(onBlur);
	const stableOnChange = useHandler(onChange);

	// (re)initialize EditorView
	useEffect(
		() => {
			const container = ref.current;

			if (container == null) {
				throw new Error("CodeMirror expects container element to exist");
			}

			const dynamicConfig = [initPromQLExtension({ lint, completionTypes }), EditorState.readOnly.of(readonly)];

			const view = viewRef.current;

			if (view == null) {
				viewRef.current = new EditorView({
					parent: container,
					state: initEditorState({
						initialValue: initialValueRef.current,
						dynamic: dynamicConfig,
						onChange: stableOnChange,
						onFocus: stableOnFocus,
						onBlur: stableOnBlur,
						onExecute: stableOnExecute,
					}),
				});
			} else {
				const transaction = view.state.update({
					effects: dynamicConfigCompartment.reconfigure(dynamicConfig),
				});

				view.dispatch(transaction);
			}
		},
		[completionTypes, lint, readonly, ref, stableOnChange, stableOnFocus, stableOnBlur, stableOnExecute],
		[isShallowEqual],
	);

	// update EditorView when value changes
	useEffect(() => {
		const view = viewRef.current;

		if (view == null || view.state.sliceDoc() === value) {
			return;
		}

		const currentSelection = view.state.selection.main;

		const transaction = view.state.update({
			// keeps the cursor/selection in place
			selection: {
				anchor: min(currentSelection.anchor, value.length),
				head: min(currentSelection.head, value.length),
			},
			changes: {
				from: 0,
				to: view.state.doc.length,
				insert: value,
			},
		});

		view.dispatch(transaction);
	}, [value]);

	useEffect(() => {
		const view = viewRef.current;

		return () => {
			view?.destroy();
		};
	}, []);

	return viewRef.current;
};

const initPromQLExtension = ({
	lint,
	completionTypes,
}: {
	lint: boolean;
	completionTypes: ReadonlyArray<CompletionType>;
}): Extension => {
	const promql = new PromQLExtension().activateCompletion(true).activateLinter(lint);

	// Create base completion strategy with remote configuration
	const baseStrategy = newCompleteStrategy({
		remote: {
			httpMethod: "GET",
			apiPrefix: "/api/metrics",
			fetchFn: fetch,
			httpErrorHandler: (error) => {
				logger.error(error);
			},
		},
	});

	// Wrap with our custom strategy that filters out functions
	const noFunctionsStrategy = new PromQLCompleteStrategy(new Set(completionTypes), baseStrategy);

	promql.setComplete({
		completeStrategy: noFunctionsStrategy,
	});

	return promql.asExtension();
};

interface InitEditorStateParams {
	initialValue: string;
	dynamic: ReadonlyArray<Extension>;
	onChange: undefined | Dispatch<string>;
	onBlur: undefined | VoidFunction;
	onFocus: undefined | VoidFunction;

	onExecute: undefined | VoidFunction;
}

const initEditorState = ({
	initialValue,
	dynamic,
	onBlur = noop,
	onFocus = noop,
	onChange = noop,
	onExecute = noop,
}: InitEditorStateParams): EditorState => {
	return EditorState.create({
		doc: initialValue,
		extensions: [
			highlightSpecialChars(),
			history(),
			indentOnInput(),
			bracketMatching(),
			closeBrackets(),
			autocompletion(),
			EditorView.lineWrapping,
			placeholder("Type your query..."),
			syntaxHighlighting(promQLHighlighter),
			dynamicConfigCompartment.of(dynamic),
			keymap.of(closeBracketsKeymap),
			keymap.of(defaultKeymap),
			keymap.of(completionKeymap),
			keymap.of(lintKeymap),
			Prec.highest(
				keymap.of([
					// This keymap is added without precedence so that closing the autocomplete dropdown
					// via Escape works without blurring the editor.
					{
						key: "Escape",
						run: ({ contentDOM }) => {
							contentDOM.blur();

							return false;
						},
					},
					{
						key: "Ctrl-Enter",
						run: () => {
							onExecute();

							return true;
						},
					},
					{
						key: "Shift-Enter",
						run: () => {
							onExecute();

							return true;
						},
					},
					{
						mac: "Meta-Enter",
						run: () => {
							onExecute();

							return true;
						},
					},
					{
						key: "Enter",
						run: insertNewlineAndIndent,
					},
				]),
			),

			EditorView.updateListener.of((update) => {
				if (update.focusChanged) {
					if (update.view.hasFocus) {
						onFocus();
					} else {
						onBlur();
					}
				}

				if (update.docChanged) {
					onChange(update.state.sliceDoc());
				}
			}),
		],
	});
};
